import { useState } from 'react';
import { Link } from 'react-router-dom';
import { detailedServices, featuredServices } from '@/mocks/suviData';
import SEOHead from '@/components/base/SEOHead';

export default function Servicios() {
  const [expandedService, setExpandedService] = useState<string | null>(null);

  const toggleService = (id: string) => {
    setExpandedService(expandedService === id ? null : id);
  };

  return (
    <>
      <SEOHead
        title="Servicios de Turismo y Tecnología: IA y Software | Suvi Digital"
        description="Marketing digital, agentes de inteligencia artificial y software a medida para agencias de viaje. Optimizamos tus procesos de forma más rápida y efectiva."
        keywords="agentes IA agencias viaje, software a medida turismo, marketing digital agencias viaje, optimización de procesos, automatización turismo, desarrollo software travel"
        canonical="https://suvidigital.com/servicios"
        schemaType="Service"
      />
      <main className="min-h-screen">
        {/* Hero */}
        <section className="relative min-h-[420px] md:min-h-[500px] flex items-center overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-br from-primary-100 via-background-50 to-secondary-100" />
          <div className="absolute top-20 right-20 w-72 h-72 bg-primary-200/30 rounded-full blur-3xl animate-float-slow" />
          <div className="absolute bottom-20 left-10 w-64 h-64 bg-secondary-200/25 rounded-full blur-3xl animate-float-delay-1" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{ backgroundImage: `radial-gradient(circle, oklch(var(--foreground-900)) 1px, transparent 1px)`, backgroundSize: '40px 40px' }}
          />

          <div className="relative z-10 w-full px-4 md:px-6 lg:px-10 xl:px-16 pt-24 md:pt-28">
            <div className="max-w-4xl">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-5 border border-primary-200/50">
                Turismo + Tecnología
              </span>
              <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-900 mb-5 leading-tight">
                Soluciones que{' '}
                <span className="text-gradient">optimizan</span>{' '}
                tus procesos
              </h1>
              <p className="text-foreground-500 text-lg md:text-xl leading-relaxed max-w-2xl">
                Combinamos marketing digital, agentes de inteligencia artificial y software a medida para que tu agencia de viaje opere de forma más rápida, efectiva y rentable.
              </p>
            </div>
          </div>
        </section>

        {/* ========== SERVICIOS PRINCIPALES ========== */}
        <section className="bg-white py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="inline-block bg-accent-100 text-accent-700 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-accent-200/50">
                Servicios Principales
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Tecnología que{' '}
                <span className="text-gradient">acelera</span> tu negocio
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                Sabemos de turismo y de tecnología. Creamos soluciones que automatizan lo manual y optimizan cada proceso para que crezcas más rápido.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
              {featuredServices.map((service, i) => (
                <div
                  key={service.id}
                  className="relative overflow-hidden rounded-3xl border border-primary-200/40 bg-gradient-to-br from-background-50 to-white p-8 md:p-10 group"
                  style={{ animation: `fade-up 0.6s ease forwards`, animationDelay: `${i * 150}ms`, opacity: 0 }}
                >
                  {/* Top gradient bar */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-primary-500 via-accent-400 to-secondary-500" />

                  {/* Decorative glow */}
                  <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-100/40 rounded-full blur-3xl group-hover:bg-primary-100/60 transition-colors duration-500" />

                  <div className="relative z-10">
                    <div className="flex items-center justify-between mb-6">
                      <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-secondary-500 text-white group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                        <i className={`${service.icon} text-2xl`} />
                      </div>
                      <span className="bg-accent-100 text-accent-700 text-xs font-semibold px-3 py-1.5 rounded-full whitespace-nowrap">
                        {service.tag}
                      </span>
                    </div>

                    <h3 className="font-heading font-bold text-2xl md:text-3xl text-foreground-900 mb-4">
                      {service.title}
                    </h3>
                    <p className="text-foreground-500 text-base leading-relaxed mb-6">
                      {service.description}
                    </p>

                    <div className="space-y-2.5 mb-8">
                      {service.features.map((feature) => (
                        <div key={feature} className="flex items-start gap-2.5 text-foreground-600 text-sm">
                          <div className="w-5 h-5 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 mt-0.5 flex-shrink-0">
                            <i className="ri-check-line text-xs" />
                          </div>
                          {feature}
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-6 border-t border-background-200/60">
                      <div>
                        <p className="text-primary-700 font-heading font-bold text-lg">
                          {service.pricing}
                        </p>
                        <p className="text-foreground-400 text-xs mt-0.5">{service.duration}</p>
                      </div>
                      <Link
                        to="/contacto"
                        className="btn-glow bg-primary-500 hover:bg-primary-600 text-white font-semibold px-6 py-3 rounded-xl text-center transition-colors whitespace-nowrap"
                      >
                        Cotizar ahora
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ========== SERVICIOS DE MARKETING DIGITAL ========== */}
        <section className="bg-background-50 py-20 md:py-28">
          <div className="px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
              <span className="inline-block bg-primary-50 text-primary-600 text-sm font-semibold px-4 py-1.5 rounded-full mb-4 border border-primary-200/50">
                Marketing Digital
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-900 mb-5">
                Todo el espectro{' '}
                <span className="text-gradient">digital</span>
              </h2>
              <p className="text-foreground-500 text-base md:text-lg leading-relaxed">
                Estrategia, branding, publicidad y más. Servicios completos para posicionar y hacer crecer tu marca turística.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {detailedServices.map((service, i) => (
                <div
                  key={service.id}
                  className="card-lift bg-white border border-background-200/60 rounded-2xl p-6 md:p-8 flex flex-col relative overflow-hidden group"
                  style={{ animationDelay: `${i * 100}ms` }}
                >
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-100/0 to-primary-100/0 group-hover:from-primary-100/50 group-hover:to-secondary-100/30 transition-all duration-500 rounded-2xl" />

                  <div className="relative z-10 flex-grow">
                    <div className="w-14 h-14 flex items-center justify-center rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 text-primary-600 mb-5 group-hover:scale-110 transition-transform duration-300">
                      <i className={`${service.icon} text-2xl`} />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-foreground-900 mb-3 group-hover:text-primary-700 transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-foreground-500 text-sm leading-relaxed mb-5">
                      {service.description}
                    </p>

                    <div className="mb-4">
                      <p className="text-foreground-800 text-sm font-semibold mb-2.5">Incluye:</p>
                      <ul className="space-y-1.5">
                        {service.features.slice(0, 4).map((feature) => (
                          <li key={feature} className="flex items-start gap-2 text-foreground-500 text-sm">
                            <i className="ri-check-line text-accent-500 mt-0.5 text-sm flex-shrink-0" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <button
                    onClick={() => toggleService(service.id)}
                    className="relative z-10 mt-4 w-full text-center bg-primary-50 hover:bg-primary-100 text-primary-700 font-semibold text-sm px-4 py-2.5 rounded-xl transition-colors border border-primary-200/50 whitespace-nowrap"
                  >
                    {expandedService === service.id ? 'Ver menos' : 'Ver detalles completos'}
                  </button>

                  {expandedService === service.id && (
                    <div className="relative z-10 mt-4 pt-4 border-t border-background-200/50 space-y-3 animate-[fade-up_0.3s_ease]">
                      {service.features.slice(4).map((feature) => (
                        <div key={feature} className="flex items-start gap-2 text-foreground-500 text-sm">
                          <i className="ri-check-line text-accent-500 mt-0.5 text-sm flex-shrink-0" />
                          {feature}
                        </div>
                      ))}
                      {service.pricing && (
                        <div className="mt-4 p-4 bg-primary-50 rounded-xl border border-primary-200/50">
                          <p className="text-foreground-700 text-sm font-semibold mb-1">{service.pricing}</p>
                          {service.duration && (
                            <p className="text-foreground-500 text-xs">{service.duration}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          <div className="absolute inset-0 animate-gradient bg-gradient-to-r from-primary-700 via-primary-600 to-secondary-600" />
          <div className="absolute top-10 left-[20%] w-64 h-64 bg-white/5 rounded-full blur-3xl animate-float" />
          <div className="absolute bottom-10 right-[20%] w-56 h-56 bg-accent-500/10 rounded-full blur-3xl animate-float-delay-1" />

          <div className="relative z-10 px-4 md:px-6 lg:px-10 xl:px-16">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5">
                ¿No encuentras lo que{' '}
                <span className="text-accent-300">buscas?</span>
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed mb-10 max-w-2xl mx-auto">
                Cada agencia de viajes tiene necesidades únicas. Hablemos y diseñemos una solución personalizada que optimice tus procesos.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/contacto"
                  className="btn-glow bg-accent-500 hover:bg-accent-600 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors whitespace-nowrap text-lg"
                >
                  Consulta Personalizada
                </Link>
                <a
                  href="https://wa.me/51989307864"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white/15 hover:bg-white/25 text-white font-semibold px-8 py-4 rounded-xl text-center transition-colors border border-white/30 whitespace-nowrap text-lg"
                >
                  <i className="ri-whatsapp-line mr-2" />
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
}